import { gql } from '@apollo/client';
import _ from 'lodash';

import {
  TAG_COUNT_FRAGMENT,
  TAG_HIGHLIGHTS_WITH_TAG_FRAGMENT,
} from '../../graphql/tags';

export const NOTE_TAG_HIGHLIGHTS_FRAGMENT = gql`
  fragment NoteTagHighlights on TestSessionNote {
    tagHighlights {
      ...TagHighlightWithTag
    }
  }
  ${TAG_HIGHLIGHTS_WITH_TAG_FRAGMENT}
`;

export const TEST_SESSION_NOTE_FRAGMENT = gql`
  fragment TestSessionNote on TestSessionNote {
    id
    videoStartTime
    videoEndTime
    videoUrl
    note
    noteType
    noteRichText
    createdAt
    shareEnabled
    shareCode
    sharePassword
    status
    createdBy {
      id
      profile {
        id
        firstName
      }
    }
    ...NoteTagHighlights
  }
  ${NOTE_TAG_HIGHLIGHTS_FRAGMENT}
`;

export const CREATE_TEST_SESSION_NOTE = gql`
  mutation CreateTestSessionNote ($testSessionId: ID!, $attributes: TestSessionNotesAttributes!) {
    createTestSessionNote(testSessionId: $testSessionId, attributes: $attributes) {
      testSessionNote {
        ...TestSessionNote
      }
    }
  }
  ${TEST_SESSION_NOTE_FRAGMENT}
`;

export const TEST_SESSION_NOTES = gql`
  query TestSessionNotes ($testSessionId: ID!, $limit: Int!, $offset: Int!, $order: String!) {
    testSessionNotes(testSessionId: $testSessionId, limit: $limit, offset: $offset, order: $order) {
      ...TestSessionNote
    }
  }
  ${TEST_SESSION_NOTE_FRAGMENT}
`;

export const DELETE_TEST_SESSION_NOTE = gql`
  mutation DeleteTestSessionNote($noteId: ID!) {
    deleteTestSessionNote(noteId: $noteId) {
      void
    }
  }
`;

export const UPDATE_TEST_SESSION_NOTE = gql`
  mutation UpdateTestSessionNote($id: ID!, $attributes: TestSessionNotesAttributes!) {
    updateTestSessionNote(id: $id, attributes: $attributes) {
      testSessionNote {
        ...TestSessionNote
      }
    }
  }
  ${TEST_SESSION_NOTE_FRAGMENT}
`;

export const TEST_SESSION_NOTE = gql`
  query TestSessionNote ($id: ID!) {
    testSessionNote(id: $id) {
      id
      videoStartTime
      videoEndTime
      videoUrl
      status
      note
      noteType
      createdAt
      shareEnabled
      shareCode
      sharePassword
      createdBy {
        id
        profile {
          id
          firstName
        }
      }
    }
  }
`;

export const DELETE_TAG_HIGHLIGHT = gql`
  mutation DeleteTagHighlight($ids: [ID!]!){
    deleteTagHighlight(ids: $ids){
      void
    }
  }
`;

export const LIMIT = 10;

export const updateNoteCreated = (testSessionId) => (cache, { data }) => {
  const { createTestSessionNote } = data;
  const { testSessionNote } = createTestSessionNote;

  const { testSessionNotes: cachedNotes } = cache.readQuery({
    query: TEST_SESSION_NOTES,
    variables: {
      testSessionId,
      limit: LIMIT,
      offset: 0,
      order: 'created_at DESC',
    },
  });

  const testSessionNotes = [testSessionNote, ...cachedNotes];

  cache.writeQuery({
    query: TEST_SESSION_NOTES,
    variables: {
      testSessionId,
      limit: LIMIT,
      offset: 0,
      order: 'created_at DESC',
    },
    data: {
      testSessionNotes,
    },
  });
};

export const updateNoteDeleted = (testSessionId, noteId) => (cache) => {
  const variables = {
    testSessionId,
    limit: LIMIT,
    offset: 0,
    order: 'created_at DESC',
  };

  // Read all highlights in note
  const { tagHighlights } = cache.readFragment({
    id: `TestSessionNote:${noteId}`,
    fragment: NOTE_TAG_HIGHLIGHTS_FRAGMENT,
    fragmentName: 'NoteTagHighlights',
  });

  // Decrease tag count for each highlight
  tagHighlights.forEach(({ tag }) => {
    const { count } = cache.readFragment({
      id: `Tag:${tag.id}`,
      fragment: TAG_COUNT_FRAGMENT,
    });

    cache.writeFragment({
      id: `Tag:${tag.id}`,
      fragment: TAG_COUNT_FRAGMENT,
      data: {
        count: count - 1,
        __typename: 'Tag',
      },
    });
  });

  // Remove Note from Query
  const { testSessionNotes: cachedNotes } = cache.readQuery({
    query: TEST_SESSION_NOTES,
    variables,
  });

  const deletedNote = _.filter(cachedNotes, (note) => note.id === noteId);

  cache.evict({ id: cache.identify(deletedNote[0]) });
  cache.gc();
};

export const updateNoteTagHighlightsDeleted = (noteId, tagHighlight) => (cache) => {
  // Remove TagHighlight from TestSessionNote

  let { tagHighlights } = cache.readFragment({
    id: `TestSessionNote:${noteId}`,
    fragment: NOTE_TAG_HIGHLIGHTS_FRAGMENT,
    fragmentName: 'NoteTagHighlights',
  });

  tagHighlights = tagHighlights.filter(({ id }) => id !== tagHighlight.id);

  cache.writeFragment({
    id: `TestSessionNote:${noteId}`,
    fragment: NOTE_TAG_HIGHLIGHTS_FRAGMENT,
    fragmentName: 'NoteTagHighlights',
    data: {
      tagHighlights,
      __typename: 'TestSessionNote',
    },
  });

  // Update Tag count
  const { count } = cache.readFragment({
    id: `Tag:${tagHighlight.tag.id}`,
    fragment: TAG_COUNT_FRAGMENT,
  });

  cache.writeFragment({
    id: `Tag:${tagHighlight.tag.id}`,
    fragment: TAG_COUNT_FRAGMENT,
    data: {
      count: count - 1,
      __typename: 'Tag',
    },
  });
};

export const updateNoteTagHighlightsCreated = (noteId) => (cache, mutationResult) => {
  const {
    data: {
      createTagHighlight: { tagHighlights },
    },
  } = mutationResult;

  const { tagHighlights: cachedHighlights } = cache.readFragment({
    id: `TestSessionNote:${noteId}`,
    fragment: NOTE_TAG_HIGHLIGHTS_FRAGMENT,
    fragmentName: 'NoteTagHighlights',
  });

  cache.writeFragment({
    id: `TestSessionNote:${noteId}`,
    fragment: NOTE_TAG_HIGHLIGHTS_FRAGMENT,
    fragmentName: 'NoteTagHighlights',
    data: {
      tagHighlights: [
        ...cachedHighlights,
        ...tagHighlights,
      ],
      __typename: 'TestSessionNote',
    },
  });
};
